import React, { Component } from 'react';
import { bool, func, object, node, number, shape, string, arrayOf } from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl, intlShape } from '../../../util/reactIntl';
import { GARDEN, propTypes } from '../../../util/types';
import { createResourceLocatorString } from '../../../util/routes';
import { routeForSearchTab } from '../../../util/searchListingTabs';
import { setIntercomLauncherHidden } from '../../../util/intercomLauncher';

import { ModalInMobile, Button, MaterialSymbol } from '../../../components';

import css from './SearchFiltersMobile.module.css';

/**
 * Barra de filtros de la búsqueda y su modal, en todos los tamaños (el nombre es de cuando
 * solo existía en móvil; ver docs/search-filters.md). La barra lleva el botón de filtros, el
 * chip del tipo de espacio y los chips rápidos; el modal, "Más utilizados", "Tipo de
 * espacio", "Ordenar por" y el resto de filtros. Los filtros se aplican al momento
 * (`liveEdit`), salvo el tipo de espacio, que espera a "Mostrar resultados".
 */
class SearchFiltersMobileComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { isFiltersOpenOnMobile: false, initialQueryParams: null };

    this.openFilters = this.openFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.closeFilters = this.closeFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
  }

  // El botón de ayuda de Intercom taparía el pie del modal ("Mostrar resultados").
  componentDidUpdate(prevProps, prevState) {
    if (prevState.isFiltersOpenOnMobile !== this.state.isFiltersOpenOnMobile) {
      setIntercomLauncherHidden(this.state.isFiltersOpenOnMobile);
    }
  }

  componentWillUnmount() {
    if (this.state.isFiltersOpenOnMobile) {
      setIntercomLauncherHidden(false);
    }
  }

  // Open filters modal, set the initial parameters to current ones
  openFilters() {
    const { onOpenModal, urlQueryParams } = this.props;
    onOpenModal();
    this.setState({ isFiltersOpenOnMobile: true, initialQueryParams: urlQueryParams });
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    const { history, onCloseModal, onCancel, routeConfiguration, params } = this.props;
    // Se queda en el tipo de espacio de la URL (antes iba siempre a `/s`, Piscinas).
    const { routeName, params: pathParams } = routeForSearchTab(params?.tab);

    history.push(
      createResourceLocatorString(
        routeName,
        routeConfiguration,
        pathParams,
        this.state.initialQueryParams
      )
    );
    if (onCancel) {
      onCancel();
    }
    onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false, initialQueryParams: null });
  }

  // Close the filter modal
  closeFilters() {
    const { onCloseModal, onShowResults } = this.props;
    onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false });
    if (onShowResults) {
      onShowResults();
    }
  }

  // Reset all filter query parameters
  resetAll(e) {
    this.props.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      rootClassName,
      className,
      children,
      sortByComponent,
      listingsAreLoaded,
      resultsCount,
      searchInProgress,
      showAsModalMaxWidth,
      onManageDisableScrolling,
      selectedFiltersCount,
      noResultsInfo,
      intl,
      params,
      spaceTypeChip,
      spaceTypeSelector,
      quickFilters,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);
    const { tab = 'pool' } = typeof params == 'object' ? params : {};
    const SearchListingType = tab == 'jardines' ? GARDEN : tab;
    const resultsMessageId =
      tab === 'todos'
        ? 'MainPanelHeader.foundallResults'
        : tab === 'interiores'
        ? 'MainPanelHeader.foundindoorsResults'
        : SearchListingType == GARDEN
        ? 'MainPanelHeader.foundgardenResults'
        : 'MainPanelHeader.foundResults';

    const resultsFound = (
      <FormattedMessage id={resultsMessageId} values={{ count: resultsCount }} />
    );
    const noResults = <FormattedMessage id="SearchFiltersMobile.noResults" />;
    const loadingResults = <FormattedMessage id="SearchFiltersMobile.loadingResults" />;
    const filtersHeading = intl.formatMessage({ id: 'SearchFiltersMobile.heading' });
    const filtersButtonLabel = intl.formatMessage(
      { id: 'SearchFiltersMobile.filtersButtonAriaLabel' },
      { count: selectedFiltersCount }
    );
    const modalCloseButtonMessage = intl.formatMessage({ id: 'SearchFiltersMobile.close' });

    const showListingsLabel = intl.formatMessage(
      { id: 'SearchFiltersMobile.showListings' },
      { count: resultsCount }
    );

    const showFilterByOrder = filters => {
      const updatedFilter = [];
      const filterNameArray = filters.map(
        filter => filter.key.split('.')[filter.key.split('.').length - 1]
      );
      const desiredArray = [
        'dates',
        'capacity',
        'category',
        'Regulation',
        'extras',
        'ownerPresent',
        'poolMaintenance',
        'discount',
        'price',
      ];
      function customSort(a, b) {
        return desiredArray.indexOf(a) - desiredArray.indexOf(b);
      }
      filterNameArray.sort(customSort);
      for (let i of filterNameArray) {
        for (let j of filters) {
          if (i == j.key.split('.')[j.key.split('.').length - 1]) {
            updatedFilter.push(j);
          }
        }
      }
      return updatedFilter;
    };

    return (
      <>
        <div className={classes}>
          <div className={css.bar}>
            <button
              type="button"
              className={css.filtersButton}
              onClick={this.openFilters}
              aria-label={filtersButtonLabel}
            >
              <MaterialSymbol name="tune" size={22} />
              {selectedFiltersCount > 0 ? (
                <span className={css.filtersCount}>{selectedFiltersCount}</span>
              ) : null}
            </button>
            {spaceTypeChip}
            {quickFilters}
          </div>

          {/* Solo montado mientras está abierto: ahora es modal en todos los tamaños, y antes
            de medir el viewport ModalInMobile lo pintaría en línea en escritorio. */}
          {this.state.isFiltersOpenOnMobile ? (
            <ModalInMobile
              id="SearchFiltersMobile.filters"
              isModalOpenOnMobile={this.state.isFiltersOpenOnMobile}
              onClose={this.cancelFilters}
              showAsModalMaxWidth={showAsModalMaxWidth}
              onManageDisableScrolling={onManageDisableScrolling}
              containerClassName={css.modalContainer}
              scrollLayerClassName={css.scrollLayer}
              // En un portal, para quedar por encima del topbar fijo en escritorio.
              usePortal
            >
              <span className={css.sheetHandle} aria-hidden="true" />
              <div className={css.modalHeadingWrapper}>
                <span className={css.modalHeading}>{filtersHeading}</span>
                <button
                  type="button"
                  className={css.closeButton}
                  onClick={this.cancelFilters}
                  aria-label={modalCloseButtonMessage}
                >
                  <MaterialSymbol name="close" size={24} />
                </button>
              </div>
              <div className={css.filtersWrapper}>
                {quickFilters ? (
                  <section className={css.section}>
                    <h3 className={css.sectionHeading}>
                      <FormattedMessage id="SearchFiltersMobile.mostUsed" />
                    </h3>
                    <div className={css.pills}>{quickFilters}</div>
                  </section>
                ) : null}
                {spaceTypeSelector ? (
                  <section className={css.section}>{spaceTypeSelector}</section>
                ) : null}
                {/* En escritorio el orden está junto al título de los resultados. */}
                {sortByComponent ? <div className={css.sortInModal}>{sortByComponent}</div> : null}
                {showFilterByOrder(children)}
              </div>

              <div className={css.showListingsContainer}>
                <button
                  type="button"
                  className={css.resetAllButton}
                  onClick={e => this.resetAll(e)}
                >
                  <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                </button>
                <Button className={css.showListingsButton} onClick={this.closeFilters}>
                  {showListingsLabel}
                </Button>
              </div>
            </ModalInMobile>
          ) : null}
        </div>

        {/* Fuera de la barra pegajosa: el recuento se va con el scroll. */}
        <div className={css.mobileOnly}>
          <div className={css.searchResultSummary}>
            {listingsAreLoaded && resultsCount > 0 ? resultsFound : null}
            {listingsAreLoaded && resultsCount === 0 ? noResults : null}
            {searchInProgress ? loadingResults : null}
          </div>
          {noResultsInfo ? noResultsInfo : null}
        </div>
      </>
    );
  }
}

SearchFiltersMobileComponent.defaultProps = {
  rootClassName: null,
  className: null,
  sortByComponent: null,
  resultsCount: null,
  searchInProgress: false,
  selectedFiltersCount: 0,
  spaceTypeChip: null,
  spaceTypeSelector: null,
  quickFilters: null,
  onShowResults: null,
  onCancel: null,
};

SearchFiltersMobileComponent.propTypes = {
  rootClassName: string,
  className: string,
  urlQueryParams: object.isRequired,
  sortByComponent: node,
  listingsAreLoaded: bool.isRequired,
  resultsCount: number,
  searchInProgress: bool,
  showAsModalMaxWidth: number.isRequired,
  onManageDisableScrolling: func.isRequired,
  onOpenModal: func.isRequired,
  onCloseModal: func.isRequired,
  resetAll: func.isRequired,
  selectedFiltersCount: number,
  spaceTypeChip: node,
  spaceTypeSelector: node,
  quickFilters: node,
  onShowResults: func,
  onCancel: func,

  // from useIntl
  intl: intlShape.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

const SearchFiltersMobile = props => {
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();

  return (
    <SearchFiltersMobileComponent
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      {...props}
    />
  );
};

export default SearchFiltersMobile;
